import React, { useState } from 'react';
import {
  useStripe, useElements,
  CardNumberElement, CardExpiryElement, CardCvcElement,
  PaymentElement, ExpressCheckoutElement, LinkAuthenticationElement,
} from '@stripe/react-stripe-js';
import styles from './StripeTestCheckout.module.scss';
import AddressAutocomplete from './AddressAutocomplete';
import PayPal from '../Checkout/PayPal';
import afterpayLogoWhite from '../../../assets/afterpay-white.png';
import afterpayLogoDark from '../../../assets/afterpay.png';
import linkLogo from '../../../assets/stripe-link.svg';

const MINIMUM_AMOUNT_FOR_AFTERPAY = 35;

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#2d3748',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      '::placeholder': { color: '#a0aec0' },
    },
    invalid: { color: '#e53e3e' },
  },
};

const CheckoutForm = ({ email, amount, onSuccess, onError, cartId, clientSecret, cart, onHeaderHiddenChange }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);

  // Step management for manual card flow
  const [step, setStep] = useState('payment'); // 'payment' | 'address' | 'contact'

  // Card form
  const [cardName, setCardName] = useState('');
  const [cardNumberComplete, setCardNumberComplete] = useState(false);
  const [cardExpiryComplete, setCardExpiryComplete] = useState(false);
  const [cardCvcComplete, setCardCvcComplete] = useState(false);
  const [cardError, setCardError] = useState('');
  const cardStepComplete = cardName.trim() && cardNumberComplete && cardExpiryComplete && cardCvcComplete;

  // Billing address — custom inputs (no Stripe AddressElement; avoids the un-removable "Full name" field)
  const [billingLine1, setBillingLine1] = useState('');
  const [billingLine2, setBillingLine2] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingState, setBillingState] = useState('');
  const [billingZip, setBillingZip] = useState('');

  // Shipping address (card flow) — custom inputs
  const [sameAsShipping, setSameAsShipping] = useState(true);
  const [shippingLine1, setShippingLine1] = useState('');
  const [shippingLine2, setShippingLine2] = useState('');
  const [shippingCity, setShippingCity] = useState('');
  const [shippingStateName, setShippingStateName] = useState('');
  const [shippingZip, setShippingZip] = useState('');

  const billingComplete = billingLine1.trim() && billingCity.trim() && billingState.trim() && billingZip.trim();
  const cardShippingComplete = shippingLine1.trim() && shippingCity.trim() && shippingStateName.trim() && shippingZip.trim();

  // Contact info
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [contactEmail, setContactEmail] = useState(email || '');
  const [contactPhone, setContactPhone] = useState('');

  // Afterpay express flow
  const [afterpaySelected, setAfterpaySelected] = useState(false);
  const showAfterpayButton = parseFloat(amount) >= MINIMUM_AMOUNT_FOR_AFTERPAY;

  // Afterpay custom address form
  const [afterpayFirstName, setAfterpayFirstName] = useState('');
  const [afterpayLastName, setAfterpayLastName] = useState('');
  const [afterpayPhone, setAfterpayPhone] = useState('');
  const [afterpayLine1, setAfterpayLine1] = useState('');
  const [afterpayLine2, setAfterpayLine2] = useState('');
  const [afterpayCity, setAfterpayCity] = useState('');
  const [afterpayState, setAfterpayState] = useState('');
  const [afterpayZip, setAfterpayZip] = useState('');

  const afterpayComplete = afterpayFirstName.trim() && afterpayLastName.trim() && afterpayPhone.trim() && afterpayLine1.trim() && afterpayCity.trim() && afterpayState.trim() && afterpayZip.trim();

  // Link express flow
  const [linkSelected, setLinkSelected] = useState(false);
  const [linkEmail, setLinkEmail] = useState(email || '');
  const [linkFirstName, setLinkFirstName] = useState('');
  const [linkLastName, setLinkLastName] = useState('');
  const [linkPhone, setLinkPhone] = useState('');
  const [linkLine1, setLinkLine1] = useState('');
  const [linkLine2, setLinkLine2] = useState('');
  const [linkCity, setLinkCity] = useState('');
  const [linkState, setLinkState] = useState('');
  const [linkZip, setLinkZip] = useState('');
  const [showLinkPayment, setShowLinkPayment] = useState(false);
  const linkShippingComplete = linkLine1.trim() && linkCity.trim() && linkState.trim() && linkZip.trim();

  // PayPal handlers
  const createPayPalOrder = (data, actions) => actions.order.create({
    purchase_units: [{ amount: { value: amount || '0.01' } }],
  });

  const handlePayPalApprove = async (data, actions) => {
    setIsProcessing(true);
    try {
      const order = await actions.order.get();
      const payer = order.payer || {};
      const shipping = order.purchase_units?.[0]?.shipping?.address || {};
      const payerName = payer.name || {};

      if (cartId) {
        sessionStorage.setItem('test_checkout_cart_id', cartId);
        sessionStorage.setItem('test_checkout_email', payer.email_address || email);
        sessionStorage.setItem('test_checkout_customer_data', JSON.stringify({
          firstName: payerName.given_name || '',
          lastName: payerName.surname || '',
          phone: payer.phone?.phone_number?.national_number || '',
          shippingAddress: {
            address1: shipping.address_line_1 || '',
            address2: shipping.address_line_2 || '',
            city: shipping.admin_area_2 || '',
            state: shipping.admin_area_1 || '',
            zip: shipping.postal_code || '',
          },
        }));
      }

      const captureResult = await actions.order.capture();
      if (captureResult.status !== 'COMPLETED') {
        onError('PayPal payment was not completed. Please try again.');
        setIsProcessing(false);
        return;
      }

      await onSuccess(
        { id: captureResult.id, paypal: true },
        {
          firstName: payerName.given_name || '',
          lastName: payerName.surname || '',
          phone: payer.phone?.phone_number?.national_number || '',
          billingAddress: {
            address1: shipping.address_line_1 || '',
            address2: shipping.address_line_2 || '',
            city: shipping.admin_area_2 || '',
            state: shipping.admin_area_1 || '',
            zip: shipping.postal_code || '',
          },
          shippingAddress: {
            address1: shipping.address_line_1 || '',
            address2: shipping.address_line_2 || '',
            city: shipping.admin_area_2 || '',
            state: shipping.admin_area_1 || '',
            zip: shipping.postal_code || '',
          },
        },
      );
      setIsProcessing(false);
    } catch (err) {
      console.error('PayPal error:', err);
      onError('An error occurred with PayPal. Please try again.');
      setIsProcessing(false);
    }
  };

  // Afterpay handler
  const handleAfterpaySubmit = async () => {
    if (!stripe || !afterpayComplete) {
      onError('Please complete the shipping address');
      return;
    }
    if (!email) {
      onError('Email is required');
      return;
    }
    setIsProcessing(true);

    if (cartId) {
      sessionStorage.setItem('test_checkout_cart_id', cartId);
      sessionStorage.setItem('test_checkout_email', email);
      sessionStorage.setItem('test_checkout_customer_data', JSON.stringify({
        firstName: afterpayFirstName,
        lastName: afterpayLastName,
        phone: afterpayPhone,
        shippingAddress: {
          address1: afterpayLine1,
          address2: afterpayLine2,
          city: afterpayCity,
          state: afterpayState,
          zip: afterpayZip,
        },
      }));
    }

    const afterpayName = `${afterpayFirstName} ${afterpayLastName}`.trim();

    try {
      const response = await fetch('/api/v1/test-payment-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: Math.round(parseFloat(amount) * 100),
          currency: 'usd',
          payment_method_type: 'afterpay_clearpay',
        }),
      });

      const { client_secret, error: intentError } = await response.json();
      if (intentError) {
        onError(intentError);
        setIsProcessing(false);
        return;
      }

      const { error: stripeError } = await stripe.confirmAfterpayClearpayPayment(client_secret, {
        payment_method: {
          billing_details: {
            name: afterpayName,
            email,
            address: {
              line1: afterpayLine1,
              line2: afterpayLine2,
              city: afterpayCity,
              state: afterpayState,
              country: 'US',
              postal_code: afterpayZip,
            },
          },
        },
        shipping: {
          name: afterpayName,
          phone: afterpayPhone,
          address: {
            line1: afterpayLine1,
            line2: afterpayLine2,
            city: afterpayCity,
            state: afterpayState,
            country: 'US',
            postal_code: afterpayZip,
          },
        },
        return_url: `${window.location.origin}/c/test-checkout/complete`,
      });

      if (stripeError) {
        sessionStorage.removeItem('test_checkout_cart_id');
        sessionStorage.removeItem('test_checkout_email');
        sessionStorage.removeItem('test_checkout_customer_data');
        onError(stripeError.message || 'Afterpay payment failed. Please try again.');
        setIsProcessing(false);
      }
    } catch (err) {
      console.error('Afterpay error:', err);
      sessionStorage.removeItem('test_checkout_cart_id');
      sessionStorage.removeItem('test_checkout_email');
      sessionStorage.removeItem('test_checkout_customer_data');
      onError('An error occurred with Afterpay. Please try again.');
      setIsProcessing(false);
    }
  };

  // Express checkout handler (Apple Pay / Google Pay)
  const handleExpressCheckoutConfirm = async (event) => {
    if (!stripe || !elements) return;
    setIsProcessing(true);

    const { billingDetails, shippingAddress: expressShipping } = event;

    if (cartId) {
      const expressEmail = billingDetails?.email || '';
      sessionStorage.setItem('test_checkout_cart_id', cartId);
      sessionStorage.setItem('test_checkout_email', expressEmail);
      const nameParts = (billingDetails?.name || '').split(' ');
      const shipping = expressShipping?.address || billingDetails?.address || {};
      sessionStorage.setItem('test_checkout_customer_data', JSON.stringify({
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
        phone: billingDetails?.phone || expressShipping?.phone || '',
        shippingAddress: {
          address1: shipping.line1 || '',
          address2: shipping.line2 || '',
          city: shipping.city || '',
          state: shipping.state || '',
          zip: shipping.postal_code || '',
        },
      }));
    }

    try {
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/c/test-checkout/complete`,
        },
        redirect: 'if_required',
      });

      if (error) {
        sessionStorage.removeItem('test_checkout_cart_id');
        sessionStorage.removeItem('test_checkout_email');
        sessionStorage.removeItem('test_checkout_customer_data');
        onError(error.message);
        setIsProcessing(false);
      } else {
        const expressEmail = billingDetails?.email || '';
        const nameParts = (billingDetails?.name || '').split(' ');
        const shipping = expressShipping?.address || billingDetails?.address || {};
        await onSuccess(null, {
          firstName: nameParts[0] || '',
          lastName: nameParts.slice(1).join(' ') || '',
          phone: billingDetails?.phone || expressShipping?.phone || '',
          billingAddress: {
            address1: (billingDetails?.address?.line1) || '',
            address2: (billingDetails?.address?.line2) || '',
            city: (billingDetails?.address?.city) || '',
            state: (billingDetails?.address?.state) || '',
            zip: (billingDetails?.address?.postal_code) || '',
          },
          shippingAddress: {
            address1: shipping.line1 || '',
            address2: shipping.line2 || '',
            city: shipping.city || '',
            state: shipping.state || '',
            zip: shipping.postal_code || '',
          },
        });
        setIsProcessing(false);
      }
    } catch (err) {
      console.error('Express checkout error:', err);
      sessionStorage.removeItem('test_checkout_cart_id');
      sessionStorage.removeItem('test_checkout_email');
      sessionStorage.removeItem('test_checkout_customer_data');
      onError('An unexpected error occurred: ' + (err.message || 'Unknown error'));
      setIsProcessing(false);
    }
  };

  // Manual card payment submit (step 3)
  const handleCardSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !clientSecret) return;
    setIsProcessing(true);

    const effectiveBilling = {
      address1: billingLine1,
      address2: billingLine2,
      city: billingCity,
      state: billingState,
      zip: billingZip,
    };
    const effectiveShipping = sameAsShipping ? effectiveBilling : {
      address1: shippingLine1,
      address2: shippingLine2,
      city: shippingCity,
      state: shippingStateName,
      zip: shippingZip,
    };

    if (cartId) {
      sessionStorage.setItem('test_checkout_cart_id', cartId);
      sessionStorage.setItem('test_checkout_email', contactEmail);
      sessionStorage.setItem('test_checkout_customer_data', JSON.stringify({
        firstName, lastName, phone: contactPhone, shippingAddress: effectiveShipping,
      }));
    }

    try {
      const cardNumberElement = elements.getElement(CardNumberElement);
      const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardNumberElement,
        billing_details: {
          name: cardName || `${firstName} ${lastName}`.trim(),
          email: contactEmail,
          address: {
            line1: billingAddressData?.address1 || '',
            line2: billingAddressData?.address2 || undefined,
            city: billingAddressData?.city || '',
            state: billingAddressData?.state || '',
            postal_code: billingAddressData?.zip || '',
            country: 'US',
          },
        },
      });

      if (pmError) {
        sessionStorage.removeItem('test_checkout_cart_id');
        sessionStorage.removeItem('test_checkout_email');
        sessionStorage.removeItem('test_checkout_customer_data');
        onError(pmError.message);
        setIsProcessing(false);
        return;
      }

      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: paymentMethod.id,
        receipt_email: contactEmail,
        shipping: {
          name: `${firstName} ${lastName}`.trim(),
          phone: contactPhone,
          address: {
            line1: effectiveShipping.address1,
            line2: effectiveShipping.address2 || undefined,
            city: effectiveShipping.city,
            state: effectiveShipping.state,
            postal_code: effectiveShipping.zip,
            country: 'US',
          },
        },
      });

      if (error) {
        sessionStorage.removeItem('test_checkout_cart_id');
        sessionStorage.removeItem('test_checkout_email');
        sessionStorage.removeItem('test_checkout_customer_data');
        onError(error.message);
        setIsProcessing(false);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        await onSuccess(paymentIntent, {
          firstName, lastName, phone: contactPhone,
          billingAddress: effectiveBilling,
          shippingAddress: effectiveShipping,
        });
        setIsProcessing(false);
      } else {
        onError('Payment completed but status is unexpected. Please contact support.');
        setIsProcessing(false);
      }
    } catch (err) {
      console.error('Payment error:', err);
      sessionStorage.removeItem('test_checkout_cart_id');
      sessionStorage.removeItem('test_checkout_email');
      sessionStorage.removeItem('test_checkout_customer_data');
      onError('An unexpected error occurred: ' + (err.message || 'Unknown error'));
      setIsProcessing(false);
    }
  };

  // Link submit
  const handleLinkSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setIsProcessing(true);

    if (cartId) {
      sessionStorage.setItem('test_checkout_cart_id', cartId);
      sessionStorage.setItem('test_checkout_email', linkEmail);
      sessionStorage.setItem('test_checkout_customer_data', JSON.stringify({
        firstName: linkFirstName,
        lastName: linkLastName,
        phone: linkPhone,
        shippingAddress: {
          address1: linkLine1,
          address2: linkLine2,
          city: linkCity,
          state: linkState,
          zip: linkZip,
        },
      }));
    }

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/c/test-checkout/complete`,
          receipt_email: linkEmail,
        },
        redirect: 'if_required',
      });

      if (error) {
        sessionStorage.removeItem('test_checkout_cart_id');
        sessionStorage.removeItem('test_checkout_email');
        sessionStorage.removeItem('test_checkout_customer_data');
        onError(error.message);
        setIsProcessing(false);
      } else {
        const linkShipping = { address1: linkLine1, address2: linkLine2, city: linkCity, state: linkState, zip: linkZip };
        await onSuccess(paymentIntent || null, {
          firstName: linkFirstName,
          lastName: linkLastName,
          phone: linkPhone,
          billingAddress: linkShipping,
          shippingAddress: linkShipping,
        });
        setIsProcessing(false);
      }
    } catch (err) {
      sessionStorage.removeItem('test_checkout_cart_id');
      sessionStorage.removeItem('test_checkout_email');
      sessionStorage.removeItem('test_checkout_customer_data');
      onError('An error occurred with Link. Please try again.');
      setIsProcessing(false);
    }
  };

  // Inline order summary for step 3
  const renderInlineOrderSummary = () => {
    if (!cart) return null;
    return (
      <div className={styles.inlineOrderSummary}>
        <h3 className={styles.inlineSummaryTitle}>Order Summary</h3>
        <div className={styles.orderTotals}>
          <div className={styles.orderTotalRow}>
            <span>Subtotal</span>
            <span>${(cart.subtotal || 0).toFixed(2)}</span>
          </div>
          {cart.couponAmount > 0 && (
            <div className={styles.orderTotalRow}>
              <span>Discount ({cart.coupon?.code})</span>
              <span className={styles.discountAmount}>-${(cart.couponAmount || 0).toFixed(2)}</span>
            </div>
          )}
          <div className={styles.orderTotalRow}>
            <span>Shipping</span>
            <span>{cart.shipping > 0 ? `$${cart.shipping.toFixed(2)}` : 'FREE'}</span>
          </div>
          <div className={styles.orderTotalRow}>
            <span>Tax</span>
            <span>${(cart.apiTax || 0).toFixed(2)}</span>
          </div>
          <div className={`${styles.orderTotalRow} ${styles.orderTotalFinal}`}>
            <span>Total</span>
            <span>${amount}</span>
          </div>
        </div>
      </div>
    );
  };

  // === AFTERPAY FLOW ===
  if (afterpaySelected) {
    return (
      <div>
        <div className={styles.section}>
          <div className={styles.stepHeader}>
            <button
              type="button"
              onClick={() => { setAfterpaySelected(false); onHeaderHiddenChange?.(false); }}
              className={styles.stepBackButton}
            >
              ←
            </button>
            <h2 className={styles.sectionTitle}>Shipping Address</h2>
          </div>
          <div className={styles.fieldRow}>
            <div className={styles.inputGroup}>
              <label className={styles.label}>First Name *</label>
              <input type="text" value={afterpayFirstName} onChange={e => setAfterpayFirstName(e.target.value)} className={styles.input} />
            </div>
            <div className={styles.inputGroup}>
              <label className={styles.label}>Last Name *</label>
              <input type="text" value={afterpayLastName} onChange={e => setAfterpayLastName(e.target.value)} className={styles.input} />
            </div>
          </div>
          <div className={styles.inputGroup}>
            <label className={styles.label}>Phone *</label>
            <input type="tel" value={afterpayPhone} onChange={e => setAfterpayPhone(e.target.value)} className={styles.input} />
          </div>
          <AddressAutocomplete
            address1={afterpayLine1}
            setAddress1={setAfterpayLine1}
            address2={afterpayLine2}
            setAddress2={setAfterpayLine2}
            city={afterpayCity}
            setCity={setAfterpayCity}
            state={afterpayState}
            setState={setAfterpayState}
            zip={afterpayZip}
            setZip={setAfterpayZip}
            label="Shipping"
          />
        </div>
        <div className={styles.section}>
          <button
            type="button"
            onClick={handleAfterpaySubmit}
            disabled={isProcessing || !stripe || !afterpayComplete}
            className={styles.afterpaySubmitButton}
          >
            {isProcessing ? 'Processing...' : (
              <>
                Continue with <img src={afterpayLogoDark} alt="Afterpay" className={styles.afterpayLogoSmall} />
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  // === LINK FLOW ===
  if (linkSelected) {
    return (
      <form onSubmit={handleLinkSubmit}>
        <div className={styles.section}>
          <div className={styles.stepHeader}>
            <button
              type="button"
              onClick={() => { setLinkSelected(false); setShowLinkPayment(false); onHeaderHiddenChange?.(false); }}
              className={styles.stepBackButton}
            >
              ←
            </button>
            <h2 className={styles.sectionTitle}>Pay with Link</h2>
          </div>
          <p className={styles.linkInfo}>
            Enter your email to use Link. If you have a Link account, we&apos;ll text you a code to autofill your saved payment info.
          </p>
          <div className={styles.fieldRow}>
            <div className={styles.inputGroup}>
              <label className={styles.label}>First Name *</label>
              <input type="text" value={linkFirstName} onChange={e => setLinkFirstName(e.target.value)} className={styles.input} required />
            </div>
            <div className={styles.inputGroup}>
              <label className={styles.label}>Last Name *</label>
              <input type="text" value={linkLastName} onChange={e => setLinkLastName(e.target.value)} className={styles.input} required />
            </div>
          </div>
          <div className={styles.inputGroup}>
            <label className={styles.label}>Phone *</label>
            <input type="tel" value={linkPhone} onChange={e => setLinkPhone(e.target.value)} className={styles.input} required />
          </div>
          <AddressAutocomplete
            address1={linkLine1}
            setAddress1={setLinkLine1}
            address2={linkLine2}
            setAddress2={setLinkLine2}
            city={linkCity}
            setCity={setLinkCity}
            state={linkState}
            setState={setLinkState}
            zip={linkZip}
            setZip={setLinkZip}
            label="Shipping"
          />
          <div className={styles.linkAuthWrapper}>
            <LinkAuthenticationElement
              options={{ defaultValues: { email: linkEmail } }}
              onChange={(e) => {
                if (e.value?.email) setLinkEmail(e.value.email);
                if (e.complete && !showLinkPayment) setShowLinkPayment(true);
              }}
            />
          </div>
          {showLinkPayment && (
            <div className={styles.paymentElement}>
              <PaymentElement
                options={{
                  layout: 'tabs',
                  defaultValues: { billingDetails: { email: linkEmail } },
                }}
              />
            </div>
          )}
        </div>
        {showLinkPayment && (
          <div className={styles.section}>
            <button
              type="submit"
              disabled={isProcessing || !stripe || !elements || !linkFirstName.trim() || !linkLastName.trim() || !linkPhone.trim() || !linkShippingComplete}
              className={styles.submitButton}
            >
              {isProcessing ? 'Processing...' : `Pay $${amount}`}
            </button>
          </div>
        )}
      </form>
    );
  }

  // === MAIN CARD FLOW (3 steps) ===
  return (
    <>
      {/* Express checkout section — shown only on step 1 */}
      {step === 'payment' && (
        <>
          <div className={styles.expressCheckoutSection}>
            <p className={styles.expressCheckoutTitle}>Express checkout</p>
            <div className={styles.expressCheckoutElementWrapper}>
              <ExpressCheckoutElement
                options={{
                  paymentMethods: { applePay: 'auto', googlePay: 'auto' },
                  layout: { overflow: 'auto' },
                }}
                onConfirm={handleExpressCheckoutConfirm}
                onCancel={() => {}}
              />
            </div>
            <div className={styles.paypalButtonRow}>
              <PayPal createOrder={createPayPalOrder} onApprove={handlePayPalApprove} />
            </div>
            <div className={styles.expressButtons}>
              <button
                type="button"
                onClick={() => { setLinkSelected(true); setAfterpaySelected(false); onHeaderHiddenChange?.(true); }}
                className={styles.linkButton}
              >
                <img src={linkLogo} alt="Link" className={styles.linkLogo} />
              </button>
              {showAfterpayButton && (
                <button
                  type="button"
                  onClick={() => { setAfterpaySelected(true); setLinkSelected(false); onHeaderHiddenChange?.(true); }}
                  className={styles.afterpayButton}
                >
                  <span className={styles.afterpayButtonText}>Checkout with </span>
                  <img src={afterpayLogoWhite} alt="Afterpay" className={styles.afterpayLogo} />
                </button>
              )}
            </div>
          </div>
          <div className={styles.divider}><span>OR</span></div>
        </>
      )}

      {/* Step 1: Payment Details */}
      {step === 'payment' && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Payment Details</h2>
          <div className={styles.inputGroup}>
            <label className={styles.label} htmlFor="cardName">Cardholder Name *</label>
            <input
              id="cardName"
              type="text"
              value={cardName}
              onChange={(e) => setCardName(e.target.value)}
              placeholder="Name on card"
              className={styles.input}
            />
          </div>
          <div className={styles.inputGroup}>
            <label className={styles.label}>Card Number *</label>
            <div className={styles.cardFieldWrapper}>
              <CardNumberElement
                options={CARD_ELEMENT_OPTIONS}
                onChange={(e) => { setCardNumberComplete(e.complete); if (e.error) setCardError(e.error.message); else setCardError(''); }}
              />
            </div>
          </div>
          <div className={styles.cardRow}>
            <div className={styles.inputGroup}>
              <label className={styles.label}>Expiration Date *</label>
              <div className={styles.cardFieldWrapper}>
                <CardExpiryElement
                  options={CARD_ELEMENT_OPTIONS}
                  onChange={(e) => { setCardExpiryComplete(e.complete); if (e.error) setCardError(e.error.message); else setCardError(''); }}
                />
              </div>
            </div>
            <div className={styles.inputGroup}>
              <label className={styles.label}>CVV *</label>
              <div className={styles.cardFieldWrapper}>
                <CardCvcElement
                  options={CARD_ELEMENT_OPTIONS}
                  onChange={(e) => { setCardCvcComplete(e.complete); if (e.error) setCardError(e.error.message); else setCardError(''); }}
                />
              </div>
            </div>
          </div>
          {cardError && <p className={styles.errorMessage}>{cardError}</p>}
          <button
            type="button"
            onClick={() => { setCardError(''); setStep('address'); }}
            disabled={!stripe || !cardStepComplete}
            className={styles.button}
          >
            Continue
          </button>
        </div>
      )}

      {/* Step 2: Address */}
      {step === 'address' && (
        <div className={styles.section}>
          <div className={styles.stepHeader}>
            <button
              type="button"
              onClick={() => setStep('payment')}
              className={styles.stepBackButton}
            >
              ←
            </button>
            <h2 className={styles.sectionTitle}>Billing Address</h2>
          </div>

          <AddressAutocomplete
            address1={billingLine1}
            setAddress1={setBillingLine1}
            address2={billingLine2}
            setAddress2={setBillingLine2}
            city={billingCity}
            setCity={setBillingCity}
            state={billingState}
            setState={setBillingState}
            zip={billingZip}
            setZip={setBillingZip}
            label="Billing"
          />

          <div className={styles.checkboxGroup}>
            <input
              type="checkbox"
              id="sameAsShipping"
              checked={sameAsShipping}
              onChange={(e) => setSameAsShipping(e.target.checked)}
            />
            <label htmlFor="sameAsShipping">Shipping address same as billing address</label>
          </div>

          {!sameAsShipping && (
            <>
              <h3 className={styles.sectionSubtitle}>Shipping Address</h3>
              <AddressAutocomplete
                address1={shippingLine1}
                setAddress1={setShippingLine1}
                address2={shippingLine2}
                setAddress2={setShippingLine2}
                city={shippingCity}
                setCity={setShippingCity}
                state={shippingStateName}
                setState={setShippingStateName}
                zip={shippingZip}
                setZip={setShippingZip}
                label="Shipping"
              />
            </>
          )}

          <button
            type="button"
            onClick={() => {
              if (!billingComplete) {
                onError('Please fill in all required billing address fields');
                return;
              }
              if (!sameAsShipping && !cardShippingComplete) {
                onError('Please fill in all required shipping address fields');
                return;
              }
              setStep('contact');
            }}
            className={styles.button}
          >
            Continue
          </button>
        </div>
      )}

      {/* Step 3: Contact Info */}
      {step === 'contact' && (
        <form onSubmit={handleCardSubmit}>
          <div className={styles.section}>
            <div className={styles.stepHeader}>
              <button
                type="button"
                onClick={() => setStep('address')}
                className={styles.stepBackButton}
              >
                ←
              </button>
              <h2 className={styles.sectionTitle}>Contact Info</h2>
            </div>

            <div className={styles.fieldRow}>
              <div className={styles.inputGroup}>
                <label className={styles.label} htmlFor="firstName">First Name *</label>
                <input
                  id="firstName"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className={styles.input}
                  required
                />
              </div>
              <div className={styles.inputGroup}>
                <label className={styles.label} htmlFor="lastName">Last Name *</label>
                <input
                  id="lastName"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className={styles.input}
                  required
                />
              </div>
            </div>
            <div className={styles.inputGroup}>
              <label className={styles.label} htmlFor="contactEmail">Email Address *</label>
              <input
                id="contactEmail"
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                className={styles.input}
                required
              />
            </div>
            <div className={styles.inputGroup}>
              <label className={styles.label} htmlFor="contactPhone">Phone Number *</label>
              <input
                id="contactPhone"
                type="tel"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                className={styles.input}
                required
              />
            </div>
          </div>

          {renderInlineOrderSummary()}

          <div className={styles.section}>
            <button
              type="submit"
              disabled={isProcessing || !stripe || !elements}
              className={styles.submitButton}
            >
              {isProcessing ? 'Processing...' : `Pay $${amount}`}
            </button>
            <p className={styles.secureCheckoutText}>
              <span>🔒</span> Secure checkout
            </p>
          </div>
        </form>
      )}
    </>
  );
};

export default CheckoutForm;
